import * as moment from "moment";
import {
  HotelCancellationPolicy,
  OperationsBookingData,
  OperationsBookingType,
  OperationsEmailDetails,
  OperationsEmailSection,
} from "../models/operations-email";

const money = (value: string | number): string => {
  if (value == null || value === "") return "No consta";
  if (typeof value === "string" && value.includes(",")) return `${value} €`;
  const amount = Number(value);
  return Number.isFinite(amount) ? `${amount.toFixed(2)} €` : `${value} €`;
};

const hotelProvider = (data: OperationsBookingData): string => {
  const providers: Record<string, string> = {
    e: "Expedia", eps: "Expedia", expedia: "Expedia",
    h: "Hotelbeds", hotelbed: "Hotelbeds", hotelbeds: "Hotelbeds",
    r: "Restel", restel: "Restel",
  };
  const provider = data.provider?.trim();
  return (provider && (providers[provider.toLowerCase()] || provider))
    || providers[data.rateKey?.charAt(0).toLowerCase()] || "No consta";
};

const hotelCancellationDetails = (data: OperationsBookingData): string[] => {
  const policies: HotelCancellationPolicy[] = Array.isArray(data.cancellationPolicies) && data.cancellationPolicies.length
    ? data.cancellationPolicies
    : Array.isArray(data.cancellationDate) ? data.cancellationDate : [];
  const descriptions = policies.map((policy) => {
    if (policy?.text?.trim()) return policy.text.trim();
    const from = policy?.from && moment(policy.from, [moment.ISO_8601, "DD/MM/YYYY", "DD-MM-YYYY"], true);
    const cost = policy?.amount != null && policy.amount !== "" ? money(policy.amount) : "";
    if (from && from.isValid()) {
      const date = new Intl.DateTimeFormat("es-ES", {
        day: "numeric", month: "long", year: "numeric",
      }).format(from.toDate());
      const deadline = `las ${from.format("HH:mm")} del ${date}`;
      return `La cancelación después de ${deadline}${cost ? ` tiene un coste de ${cost}.` : ": coste no disponible."}`;
    }
    return cost ? `Coste de cancelación: ${cost}. Fecha no disponible.` : "";
  }).filter(Boolean);
  if (descriptions.length) return Array.from(new Set(descriptions));

  const legacy = [data.cancellationPolicies, data.cancellationDate]
    .filter((value): value is string => typeof value === "string" && !!value.trim());
  return Array.from(new Set(legacy.reduce<string[]>((result, value) => {
    const date = moment(value, [moment.ISO_8601, "DD/MM/YYYY", "DD-MM-YYYY"], true);
    return result.concat(date.isValid()
      ? hotelCancellationDetails({ cancellationPolicies: [{ from: value }] })
      : [value.trim()]);
  }, [])));
};

export const buildOperationsEmailDetails = (
  type: OperationsBookingType,
  data: OperationsBookingData
): OperationsEmailDetails => {
  const sections: OperationsEmailSection[] = [];
  const section = (title: string): OperationsEmailSection => {
    const result: OperationsEmailSection = { title, rows: [] };
    sections.push(result);
    return result;
  };
  const row = (target: OperationsEmailSection, label: string, value: string | number) => {
    if (value != null && String(value).trim() !== "") {
      target.rows.push({ label, value: String(value) });
    }
  };
  const summary = section("Resumen de la reserva");
  const bookedFlight = type === "flight" && data.lleegoBookingID != null;
  row(summary, "Servicio", type === "hotel" ? "Hotel" : type === "flight" ? "Vuelo" : "Tren");
  row(summary, "Estado", bookedFlight
    ? "Vuelo reservado. Pendiente de gestionar asientos."
    : "Reserva pendiente de gestión por operaciones (letItGo).");
  row(summary, "Origen de la solicitud", "WEB");
  if (data.unavail) row(summary, "Disponibilidad", "Sin disponibilidad para la opción seleccionada.");

  if (type === "hotel") {
    const hotel = data.hotel || {};
    const room = hotel.room || {};
    row(summary, "Hotel", hotel.name);
    row(summary, "Proveedor", hotelProvider(data));
    row(summary, "Dirección", hotel.address);
    row(summary, "Entrada", data.checkIn);
    row(summary, "Salida", data.checkOut);
    row(summary, "Precio total", money(data.totalPrice));
    const rooms = section("Habitaciones y condiciones");
    row(rooms, "Número de habitaciones", hotel.totalRooms);
    row(rooms, "Adultos", room.adults);
    row(rooms, "Habitación", room.roomName || "No consta");
    if (room.roomNumber != null) row(rooms, "Número de habitación", room.roomNumber + 1);
    row(rooms, "Tipo de cama", room.bedType);
    if (room.price != null) row(rooms, "Precio de la habitación", money(room.price));
    row(rooms, "Régimen", data.boardName);
    row(rooms, "Desayuno incluido", data.breakfast == null ? "No consta" : data.breakfast ? "Sí" : "No");
    row(rooms, "Cancelación incluida", data.cancellation == null ? "No consta" : data.cancellation ? "Sí" : "No");
    hotelCancellationDetails(data).forEach((condition, index) => {
      row(rooms, `Condición de cancelación ${index + 1}`, condition);
    });
    const guests = section("Huéspedes");
    (data.guests || []).forEach((guest, index) => {
      row(guests, `Huésped ${index + 1}`, `${guest.name || ""} ${guest.surname || ""}`.trim());
    });
    row(rooms, "Comentarios para el hotel", data.comments);
  } else {
    row(summary, "Itinerario", data.itinerary);
    row(summary, bookedFlight ? "Fechas (salida ida - llegada vuelta)" : "Fechas (salida ida - salida vuelta)", data.dates);
    row(summary, "Precio total", money(data.price));
    row(summary, "Pasajeros", Array.isArray(data.travellers) ? data.travellers.join("\n") : data.travellers);
    row(summary, type === "flight" ? "Escalas" : "Número de tramos", data.stops);
    row(summary, type === "flight" ? "Detalle de escalas" : "Detalle de tramos", data.stopsText);

    if (type === "flight") {
      row(summary, "Localizador", data.locator);
      row(summary, "ID de Lleego", data.lleegoBookingID);
      row(summary, "Tarifa", data.fares?.fareName);
      if (data.fares?.price != null) row(summary, "Precio de la tarifa", money(data.fares.price));
      row(summary, "Descuento por residente", data.canUseResident);
      const travellers = section("Viajeros");
      (data.travelerList || []).forEach((traveller, index) => {
        row(travellers, `Viajero ${index + 1}`, `${traveller.name || ""} ${(traveller.surnames || []).join(" ")}`.trim());
        row(travellers, `Tarjeta de vuelo del viajero ${index + 1}`, traveller.flightCard || "No consta");
      });
      const seats = section("Asientos");
      if (Array.isArray(data.seats)) {
        const groups = new Map<string, { label: string; seats: string[]; price: number }>();
        data.seats.forEach((seat) => {
          const key = JSON.stringify([seat.segment, seat.departure, seat.arrival]);
          const group = groups.get(key) || {
            label: `Trayecto ${seat.segment ?? ""} (${seat.departure || ""} → ${seat.arrival || ""})`,
            seats: [], price: 0,
          };
          group.seats.push(`${seat.row ?? ""}${seat.column || ""} (${money(seat.price ?? 0)})`);
          group.price += Number(seat.price ?? 0);
          groups.set(key, group);
        });
        groups.forEach((group) => {
          row(seats, group.label, `${group.seats.join("\n")}\nTotal asientos trayecto: ${money(group.price)}`);
        });
      } else if (typeof data.seats === "string") {
        row(seats, "Asientos seleccionados", data.seats);
      }
      const ancillaries = [
        { title: "Equipaje", items: data.baggages || [] },
        { title: "Prioridades de embarque", items: data.priority || [] },
      ];
      ancillaries.forEach(({ title, items }) => {
        const extras = section(title);
        items.forEach((item) => {
          const unit = Number(item.unitPrice ?? item.price ?? 0);
          const count = Number(item.paxCount ?? (title === "Prioridades de embarque" ? item.paxRefs?.length ?? 1 : 1));
          const total = item.totalPrice ?? unit * count;
          row(extras, `${item.name || title}${item.journey ? ` · ${item.journey}` : ""}`,
            `${money(unit)} por pasajero\nCantidad: ${count}\nTotal: ${money(total)}`);
        });
      });
    } else if (type === "train") {
      const seats = !Array.isArray(data.seats) && typeof data.seats !== "string" ? data.seats : undefined;
      (["outward", "return"] as const).forEach((journey) => {
        const label = journey === "outward" ? "ida" : "vuelta";
        row(summary, `Tarifa de ${label}`, data.fares?.[journey]);
        const selectedSeats = section(`Asientos de ${label}`);
        (seats?.[journey] || []).forEach((seat, index) => {
          row(selectedSeats, `Asiento ${index + 1}`, `Vagón ${seat.carriage ?? ""} · Asiento ${seat.position ?? ""}\nTramo: ${seat.itinerary || "No consta"}`);
        });
        const extras = section(`Extras de ${label}`);
        (data.extras?.[journey] || []).forEach((extra, index) => {
          row(extras, `Extra ${index + 1}`, `${extra.quantity ?? ""} de ${extra.description || ""}\nTramo: ${extra.itinerary || "No consta"}`);
        });
      });
    }
  }

  const management = section("Datos de gestión");
  row(management, "Autor de la reserva", data.author);
  row(management, "Centro de coste", data.costCenter);
  row(management, "Código interno", data.customCode);
  row(management, "Proyecto", data.project);
  row(management, "Seguro", data.insurance);
  row(management, "Anotaciones y extras solicitados", data.annotationsExtras);
  row(management, "Acción pendiente", bookedFlight
    ? "El usuario espera la confirmación de sus asientos. Si no hay disponibilidad, contacte con él para recomendarle una opción que se ajuste a sus preferencias."
    : "El usuario espera la subida de su bono. Si no hay disponibilidad, contacte con él para recomendarle una opción que se ajuste a su búsqueda.");

  return { type, sections: sections.filter((item) => item.rows.length > 0) };
};

export const buildOperationsEmailText = (details: OperationsEmailDetails): string =>
  details.sections.map((section) =>
    `${section.title}\n${section.rows.map((row) => `${row.label}: ${row.value}`).join("\n")}`
  ).join("\n\n");
